'use client';
import { Box, Users, Zap } from 'lucide-react';
import { useT } from '../../i18n';

const ArsenalSection = () => {
  const t = useT();

  const products = [
    {
      title: t('arsenal.blindbox.title'),
      subtitle: t('arsenal.blindbox.subtitle'),
      price: 'HK$800',
      priceNote: t('arsenal.priceFrom'),
      description: t('arsenal.blindbox.desc'),
      features: [
        t('arsenal.blindbox.f1'),
        t('arsenal.blindbox.f2'),
        t('arsenal.blindbox.f3'),
        t('arsenal.blindbox.f4'),
        t('arsenal.blindbox.f5'),
      ],
      popular: true,
      icon: <Box className="w-8 h-8" />,
      demoUrl: 'https://cny.jkdcoding.com/',
      image: '/images/rlhamg7rb3.png',
    },
    {
      title: t('arsenal.whatsapp.title'),
      subtitle: t('arsenal.whatsapp.subtitle'),
      price: 'HK$1,500',
      priceNote: t('arsenal.priceFrom'),
      description: t('arsenal.whatsapp.desc'),
      features: [
        t('arsenal.whatsapp.f1'),
        t('arsenal.whatsapp.f2'),
        t('arsenal.whatsapp.f3'),
        t('arsenal.whatsapp.f4'),
        t('arsenal.whatsapp.f5'),
      ],
      icon: <Users className="w-8 h-8" />,
      demoUrl: 'https://deliciousexp.jkdcoding.com/',
      image: '/images/ya65tqp4mt.png',
    },
  ];

  return (
    <section id="arsenal" className="py-12 lg:py-16 relative noise-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-2 bg-jkd-gray-100 text-jkd-gold text-sm font-mono tracking-widest mb-4 rounded">
            {t('arsenal.tag')}
          </span>
          <h2 className="section-title text-white">
            <span className="text-gradient-gold">{t('arsenal.title')}</span>
          </h2>
          <p className="section-subtitle mx-auto mt-4">
            {t('arsenal.subtitle')}
          </p>
          <div className="divider-gold mx-auto mt-6" />
        </div>

        {/* Product Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {products.map((product, index) => (
            <div
              key={index}
              className={`relative group card-dark ${
                product.popular ? 'card-gold-border glow-gold' : ''
              }`}
            >
              {/* Popular Badge */}
              {product.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-jkd-gold text-jkd-black text-xs font-bold tracking-wider">
                    {t('arsenal.blindbox.subtitle')}
                  </span>
                </div>
              )}

              {/* Icon */}
              <div className="text-jkd-gold mb-4 group-hover:scale-110 transition-transform duration-300">
                {product.icon}
              </div>

              {/* Title & Subtitle */}
              <div className="mb-4">
                <h3 className="text-xl font-bold text-white mb-1">{product.title}</h3>
                <p className="text-gray-500 text-sm">{product.subtitle}</p>
              </div>

              {/* Price */}
              <div className="mb-4">
                <div className="flex items-baseline">
                  <span className="text-2xl font-black text-jkd-gold">{product.price}</span>
                  <span className="text-gray-500 text-sm ml-2">{product.priceNote}</span>
                </div>
              </div>

              {/* Description */}
              <p className="text-gray-300 text-sm mb-4 leading-relaxed">{product.description}</p>

              {/* Features */}
              <ul className="space-y-2 mb-6">
                {product.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-start gap-2 text-sm">
                    <span className="text-jkd-gold mt-0.5">✓</span>
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Demo Image */}
              {product.image && (
                <div className="mt-6 rounded-lg overflow-hidden border border-jkd-gray-200">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-auto"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Promise Badge */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-jkd-gray-100 rounded-lg border border-jkd-gray-200">
            <Zap className="w-5 h-5 text-jkd-gold" />
            <span className="text-gray-300">
              <span className="text-jkd-gold font-bold">{t('arsenal.promise')}</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArsenalSection;
